import { useEffect, useRef } from 'react';

const eventTypes = [
  { title: 'Cafés da Manhã de Escritório', desc: 'Aquela pausa que alinha a equipe antes do dia começar.' },
  { title: 'Reuniões e Alinhamentos', desc: 'Sanduíches embalados a vácuo, fáceis de servir sem interromper a agenda.' },
  { title: 'Capacitações e Imersões', desc: 'Refeições leves para manter o foco durante o aprendizado.' },
  { title: 'Feiras e Estandes', desc: 'Atendimento em grande volume sem perder o padrão Naturalle.' },
  { title: 'Workshops Criativos', desc: 'Sabores que acompanham a energia de um dia produtivo.' },
  { title: 'Colégios e Cursos', desc: 'Lanches balanceados e higiênicos para a rotina de estudo.' },
  { title: 'Campi Universitários', desc: 'Opcões práticas para quem vive entre uma aula e outra.' },
  { title: 'Ativações de Marca', desc: 'Degustações que aproximam o público da sua marca.' },
  { title: 'Torneios e Competições', desc: 'Nutrição que repõe energia antes e depois de cada disputa.' },
  { title: 'Comemorações de Equipe', desc: 'Aquele momento de descontrair junto, com comida de verdade.' },
  { title: 'Instituições e Órgãos Públicos', desc: 'Parceria consistente para demandas recorrentes.' },
];

const EventsSection = () => {
  const sectionRef = useRef<HTMLElement>(null);

  useEffect(() => {
    const section = sectionRef.current;
    if (!section) return;

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            section.querySelectorAll('.reveal').forEach((el) => {
              el.classList.add('revealed');
            });
          }
        });
      },
      { threshold: 0.08 }
    );
    observer.observe(section);
    return () => observer.disconnect();
  }, []);

  return (
    <section
      id="eventos"
      ref={sectionRef}
      className="relative py-20 md:py-28 bg-background-100 overflow-hidden"
    >
      {/* Subtle top divider */}
      <div className="absolute top-0 left-0 right-0 h-px bg-background-200/60" />

      <div className="relative z-10 w-full px-6 md:px-10 lg:px-16">
        <div className="mx-auto max-w-7xl">

          {/* ===== Editorial header ===== */}
          <div className="grid gap-10 lg:grid-cols-12 lg:gap-16 mb-16 md:mb-24">
            {/* Left — label + giant title */}
            <div className="lg:col-span-6">
              <span className="reveal mb-6 inline-flex items-center gap-2 font-label text-xs font-medium uppercase tracking-[0.3em] text-secondary-500 md:text-sm">
                <span className="h-px w-8 bg-secondary-500/60" />
                Serviço de Eventos
              </span>
              <h2 className="reveal reveal-delay-1 font-display text-4xl leading-[1.05] text-foreground-950 md:text-6xl lg:text-[72px]">
                Onde cada{' '}
                <span className="italic text-accent-400">ocasião</span>{' '}
                vira uma memória
              </h2>
            </div>

            {/* Right — pull quote + image */}
            <div className="lg:col-span-6">
              <p className="reveal reveal-delay-2 font-display text-xl leading-snug text-foreground-600 md:text-2xl mb-8 md:mb-10">
                Mais do que servir alimentos, a Naturalle leva praticidade, organização e sabor para cada encontro — do coffee break ao grande evento.
              </p>

              <div className="reveal reveal-delay-3 relative">
                <div className="relative overflow-hidden rounded-2xl">
                  <img
                    src="https://storage.readdy-site.link/project_files/46b11d92-1101-4f92-aa67-f7a4033739d8/8693d164-949c-450b-a22d-7b1e9bd7a9ec_compressed_Imagem-1-gerada-10.webp"
                    alt="Catering corporativo premium da Naturalle"
                    title="Catering corporativo — Naturalle Sanduíches"
                    className="w-full h-[260px] md:h-[340px] object-cover"
                  />
                  {/* Offset gold frame */}
                  <div className="pointer-events-none absolute -bottom-3 -right-3 h-full w-full rounded-2xl border border-secondary-500/40" />
                </div>
              </div>
            </div>
          </div>

          {/* ===== Editorial index of occasions ===== */}
          <div className="mb-20 md:mb-28">
            <div className="mb-6 flex items-end justify-between md:mb-8">
              <h3 className="reveal font-heading text-xl text-foreground-900 md:text-2xl">
                Ocasiões que atendemos
              </h3>
              <span className="reveal font-label text-xs uppercase tracking-[0.25em] text-foreground-400 md:text-sm">
                {String(eventTypes.length).padStart(2, '0')} formatos
              </span>
            </div>

            <div className="border-t border-foreground-100">
              {eventTypes.map((evt, index) => (
                <div
                  key={evt.title}
                  className="reveal group relative flex items-baseline gap-5 border-b border-foreground-100 py-6 transition-colors duration-500 hover:bg-background-50/70 md:gap-10 md:py-7"
                  style={{ transitionDelay: `${(index % 6) * 0.05}s` }}
                >
                  {/* Ghost number */}
                  <span className="select-none font-display text-3xl leading-none text-foreground-200 transition-colors duration-500 group-hover:text-accent-400 md:text-4xl">
                    {String(index + 1).padStart(2, '0')}
                  </span>

                  {/* Title + desc */}
                  <div className="flex-1">
                    <h4 className="font-display text-2xl leading-tight text-foreground-900 transition-all duration-500 group-hover:translate-x-1 group-hover:text-accent-400 md:text-3xl">
                      {evt.title}
                    </h4>
                    <p className="mt-1 font-body text-sm leading-relaxed text-foreground-500 md:text-base">
                      {evt.desc}
                    </p>
                  </div>

                  {/* Arrow */}
                  <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full border border-background-200 text-foreground-400 opacity-0 -translate-x-2 transition-all duration-500 group-hover:translate-x-0 group-hover:opacity-100 group-hover:border-secondary-500/50 group-hover:text-secondary-500">
                    <i className="ri-arrow-right-up-line" />
                  </span>
                </div>
              ))}
            </div>
          </div>

          {/* ===== CTA Banner ===== */}
          <div className="reveal relative overflow-hidden rounded-3xl">
            <div className="absolute inset-0 bg-accent-500" />
            <div
              className="absolute inset-0 opacity-10"
              style={{ backgroundImage: 'radial-gradient(circle at 2px 2px, white 1px, transparent 0)', backgroundSize: '24px 24px' }}
            />

            <div className="relative z-10 flex flex-col items-center justify-between gap-8 p-8 md:flex-row md:p-12 lg:p-16">
              <div className="text-center md:text-left">
                <h3 className="font-display text-2xl leading-tight text-white md:text-3xl lg:text-4xl">
                  Seu evento merece uma experiência à altura
                </h3>
                <p className="mt-3 max-w-xl font-body text-sm leading-relaxed text-white/80 md:text-base">
                  Da reunião de equipe aos grandes eventos corporativos, a Naturalle cuida da alimentação para que você possa focar no que realmente importa.
                </p>
              </div>
              <a
                href="https://wa.me/5585982245030?text=Olá!%20Gostaria%20de%20solicitar%20um%20orçamento%20para%20evento%20da%20Naturalle%20Sanduíches."
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 whitespace-nowrap rounded-full bg-foreground-950 px-8 py-4 font-label text-sm text-background-50 transition-all duration-300 hover:bg-foreground-800"
              >
                <span>Solicitar orçamento</span>
                <i className="ri-arrow-right-line" />
              </a>
            </div>
          </div>

        </div>
      </div>
    </section>
  );
};

export default EventsSection;